import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Task } from '../types/tasks';
import {
    changeCustomTaskStatus,
    createCustomTask,
    deleteTask,
    fetchTasks,
    setIsCompletedTask,
    updateCustomTask,
} from '../../api';
import { fetchGanttTasks } from '../../api/fetchGanttTasks';

const tasksAdapter = createEntityAdapter<Task>({
    selectId: (task) => task.uid,
});

const initialState = tasksAdapter.getInitialState({
    isLoading: false,
    isStatusLoading: false,
    isModalLoading: false,
    isModalError: null,
    error: null as string | null,
});

export const taskSlice = createSlice({
    name: 'tasks',
    initialState,
    reducers: {
        addTasks: tasksAdapter.setAll,
        addOneTask: tasksAdapter.addOne,
        removeTask: tasksAdapter.removeOne,
        updateTask: tasksAdapter.updateOne,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGanttTasks.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchGanttTasks.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Не удалось загрузить задачи';
            })
            .addCase(fetchGanttTasks.fulfilled, (state, action: PayloadAction<Task[]>) => {
                state.isLoading = false;
                state.error = null;
                tasksAdapter.setAll(state, action.payload);
            })
            .addCase(fetchTasks.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTasks.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                tasksAdapter.setAll(state, action.payload);
            })
            .addCase(fetchTasks.rejected, (state) => {
                state.isLoading = false;
                //todo МЗ вставить сообщение с сервера
                state.error = 'Ошибка загрузки задач';
            })
            .addCase(deleteTask.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.isModalLoading = false;
                state.isModalError = null;
                tasksAdapter.removeOne(state, action.payload);
            })
            .addCase(deleteTask.pending, (state) => {
                state.error = null;
                state.isModalLoading = true;
                state.isModalError = null;
            })
            .addCase(deleteTask.rejected, (state, { payload }) => {
                state.isLoading = false;
                //todo МЗ вставить сообщение с сервера
                state.error = 'Ошибка удаления задачи';
                state.isModalLoading = false;
                state.isModalError = payload ?? 'Ошибка создания задачи';
            })
            .addCase(createCustomTask.pending, (state) => {
                state.isModalLoading = true;
                state.isModalError = null;
            })
            .addCase(createCustomTask.fulfilled, (state, action) => {
                state.isModalLoading = false;
                state.isModalError = null;
                tasksAdapter.addOne(state, action.payload.task);
            })
            .addCase(createCustomTask.rejected, (state, { payload }) => {
                state.isModalLoading = false;
                state.isModalError = payload ?? 'Ошибка создания задачи';
            })
            .addCase(updateCustomTask.pending, (state) => {
                state.isModalLoading = true;
                state.isModalError = null;
            })
            .addCase(updateCustomTask.fulfilled, (state, action) => {
                state.isModalLoading = false;
                state.isModalError = null;

                tasksAdapter.updateOne(state, {
                    id: action.payload.task.uid,
                    changes: action.payload.task,
                });
            })
            .addCase(updateCustomTask.rejected, (state, { payload }) => {
                state.isModalLoading = false;
                state.isModalError = payload ?? 'Ошибка изменения задачи';
            })
            .addCase(changeCustomTaskStatus.pending, (state, action) => {
                state.error = null;
                const task = state.entities[action.meta.arg.taskId]; // Получаем задачу по ID
                if (task) {
                    tasksAdapter.updateOne(state, {
                        id: action.meta.arg.taskId,
                        changes: { isStatusLoading: true },
                    });
                }
            })
            .addCase(changeCustomTaskStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isStatusLoading = false;
                state.error = null;
                const task = state.entities[action.payload.taskId];
                tasksAdapter.updateOne(state, {
                    id: action.payload.taskId,
                    changes: {
                        ...task,
                        context: { ...task.context, status: action.payload.switcherStatus },
                        isStatusLoading: false,
                    },
                });
            })
            .addCase(changeCustomTaskStatus.rejected, (state, { payload }) => {
                state.isLoading = false;
                state.isStatusLoading = false;
                state.error = 'Ошибка изменения статуса задачи';
            })
            .addCase(setIsCompletedTask.pending, (state) => {
                state.error = null;
            })
            .addCase(setIsCompletedTask.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                const task = state.entities[action.payload.taskId];
                tasksAdapter.updateOne(state, {
                    id: action.payload.taskId,
                    changes: {
                        ...task,
                        completed: action.payload.is_completed,
                    },
                });
            });
    },
});

//@ts-ignore
export const tasksSelector = tasksAdapter.getSelectors((state) => state.tasksReducer);

export const { actions: tasksActions } = taskSlice;
export const { reducer: tasksReducer } = taskSlice;
